import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Flex, Form, message, Modal, Space, Input, Radio, Select, Switch } from "antd"
import WorkflowCard from "./WorkflowCard";
import Wrapper from "../form-editor/Preview/Wrapper";
import { CheckOutlined, CloseOutlined, EditOutlined, ShareAltOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { showLoader } from "../../utils/showloader";
import { addCollaboratorInSpace, submitRequestForm } from "../../services/requestServices";
import TextArea from "antd/es/input/TextArea";
import SubmitMessage from "./SubmitMessage";
import { buildSkippedStepData, canSendStepWaitingNotification, getApproversWithDelegated, getDifferenceValues, getRequestApprovers, getStepApprovers, getStepFormValues, getUniqueStepUsers, getVisibleWidgets, hasUserActedOnCurrentStep, isObjectEmpty, isUserApprover, nodeConditionsHandler, valueModifier } from "../../utils/requestHelper";
import { approvalDelayed, flattenFormItems, formatPostValue, getAllWidgets, getDueDate, getPreviousStepFrozenFields, isValueEmpty } from "../../utils/formsHelper";
import { triggerNotification } from "./triggerNotification";
import { sortNodesByEdges } from "../../utils/workflowHelper";
import { shareOngoingPublicLink } from "../../services/publicFormServices";
import { useTranslation } from "react-i18next";
import { resolveFieldTranslation, FORM_LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from "../../utils/formTranslations";
import { resolveNodeTranslation } from "../../utils/workflowTranslations";
import { trimStringValues } from "../../utils/trimStrings";
import { getStoredCompanyIdentifier } from "../../utils/companyIdentifier";

const FOCUSABLE_FIELD_SELECTOR = 'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), .ant-select-selector, .ant-picker, [tabindex]:not([tabindex="-1"])';

const expandCollapsedAncestors = (el) => {
    const collapsedItems = [];
    let collapseItem = el?.closest?.('.ant-collapse-item');
    while (collapseItem) {
        if (!collapseItem.classList.contains('ant-collapse-item-active')) {
            collapsedItems.push(collapseItem);
        }
        collapseItem = collapseItem.parentElement?.closest('.ant-collapse-item');
    }
    collapsedItems.reverse().forEach((item) => {
        const expandIcon = item.querySelector(':scope > .ant-collapse-header .ant-collapse-expand-icon');
        const header = item.querySelector(':scope > .ant-collapse-header');
        (expandIcon || header)?.click();
    });
    return collapsedItems.length > 0;
};

const getScrollParent = (el) => {
    let parent = el?.parentElement;
    while (parent && parent !== document.body) {
        const style = window.getComputedStyle(parent);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
};

const scrollToFirstMissingField = (form, errorInfo, root) => {
    const firstFieldName = errorInfo?.errorFields?.[0]?.name;
    const scope = root || document;

    window.setTimeout(() => {
        const errorItem = scope.querySelector('.ant-form-item-has-error');
        const didExpand = errorItem ? expandCollapsedAncestors(errorItem) : false;

        const scrollIntoPlace = () => {
            const target = scope.querySelector('.ant-form-item-has-error') || errorItem;
            if (target && typeof target.scrollIntoView === 'function') {
                const scrollParent = getScrollParent(target) || target.closest('.request-details') || target.closest('.approval-details');
                if (scrollParent && typeof scrollParent.scrollTo === 'function') {
                    const parentRect = scrollParent.getBoundingClientRect();
                    const targetRect = target.getBoundingClientRect();
                    const nextTop = scrollParent.scrollTop + (targetRect.top - parentRect.top) - (parentRect.height / 2) + (targetRect.height / 2);
                    scrollParent.scrollTo({ top: Math.max(0, nextTop), behavior: 'smooth' });
                } else {
                    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                const focusable = target.querySelector(FOCUSABLE_FIELD_SELECTOR);
                if (focusable && typeof focusable.focus === 'function') {
                    focusable.focus({ preventScroll: true });
                }
                return;
            }
            if (firstFieldName && form?.scrollToField) {
                form.scrollToField(firstFieldName, { behavior: 'smooth', block: 'center' });
            }
        };

        if (didExpand) {
            window.setTimeout(scrollIntoPlace, 250);
        } else {
            scrollIntoPlace();
        }
    }, 50);
};

const ApprovalDetails = (props) => {
    const { workflowData, requestDetail, requestForm, currentStatus, defaultRequestDetail, workflowConditions, currentActiveForm, conditions = [], spaceData = {}, currentValues = {}, definedWidgets = [], notificationTemplates = [], getRequestDetail, ...rest } = props
    const { t, i18n } = useTranslation()
    const localizedFormHeader = resolveFieldTranslation(currentActiveForm?.formHeader || {}, i18n.language)
    const { userInfo, userGroups } = useSelector(state => state.user)
    const { spaceSpecificGroups = [], spaceSpecificUsers = [] } = useSelector(state => state.userRoleGroupState)
    const [currentStep, setCurrentStep] = useState({})
    // Resolve the step's localized name and approve/reject button labels for the
    // viewer's language. Logic elsewhere still relies on the canonical
    // `currentStep.name`, so this is used for display only.
    const localizedStep = resolveNodeTranslation(currentStep, i18n.language)
    const [currentAssignee, setCurrentAssignee] = useState("")
    const [showRefusalModal, setShowRefusalModal] = useState(false)
    const [isApproving, setIsApproving] = useState(true)
    const [returnSteps, setReturnSteps] = useState([])
    const [showShareModal, setShowShareModal] = useState(false)
    const [showCollaboratorModal, setShowCollaboratorModal] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [recipients, setRecipients] = useState("")
    const [shareLanguage, setShareLanguage] = useState(DEFAULT_LANGUAGE)
    const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false)
    const [allowPreviousForms, setAllowPreviousForms] = useState(false)
    const [collaborators, setCollaborators] = useState([{ name: "", email: "" }])
    const [collaboratorErrors, setCollaboratorErrors] = useState([])

    const [messageState, setMessageState] = useState(false)
    const [submitMessage, setSubmitMessage] = useState("")
    const [form] = Form.useForm();
    const approvalFormRef = useRef(null);
    // `preserve: true` reads the whole form store rather than the registered-field
    // snapshot. A hidden field is unmounted, so it drops out of the plain snapshot -
    // a condition that reads that field would then see it as empty and stop firing,
    // even though the form still holds its value. Conditions have to be evaluated
    // against the full store.
    let formValues = Form.useWatch([], { form, preserve: true }) || {};
    const [refusalForm] = Form.useForm();
    let refusalFormValues = Form.useWatch([], refusalForm);
    const currentFormConditions = conditions.filter(condition => condition.formId === currentActiveForm?.formId)
    // When the viewed cycle differs from the request's current cycle, the user is
    // looking at a past cycle and all form fields should be disabled.
    const hasMultipleCycles = defaultRequestDetail?.logs && Object.keys(defaultRequestDetail.logs).length > 0
    const isNonCurrentCycleView = hasMultipleCycles && requestDetail?.currentCycle !== defaultRequestDetail?.currentCycle
    const hasSubmittedCurrentStep = hasUserActedOnCurrentStep(defaultRequestDetail, userInfo)
    const freezeFields = useMemo(() => {
        const workflow = requestDetail?.workflow || defaultRequestDetail?.workflow || []
        if (requestDetail?.status === "draft" || !workflow.length) {
            return {}
        }
        return getPreviousStepFrozenFields(workflow)
    }, [requestDetail, defaultRequestDetail])
    const allFormElements = useMemo(() => {
        const otherForms = Array.isArray(rest.forms) ? rest.forms : []
        const forms = currentActiveForm?.items
            ? [currentActiveForm, ...otherForms.filter((form) => form?.formId !== currentActiveForm.formId)]
            : otherForms
        return flattenFormItems(forms)
    }, [rest.forms, currentActiveForm])

    useEffect(() => {
        if (defaultRequestDetail.requestId) {
            let requestWorkflow = defaultRequestDetail.workflow
            let docStep = requestWorkflow[requestWorkflow.length - 1]
            // The step's own `formValues` is only a partial, point-in-time capture:
            // submitRequest writes it when someone acts on the step, and Save merges
            // in just the fields it worked out as changed and visible
            // (`modCurrentValues` in handleDocSave). The request-level `value` is the
            // one that always holds the full accumulated record, so it is layered on
            // top and is what the form is filled from; the step snapshot only supplies
            // keys `value` has nothing for. Empty values are skipped so they do not
            // blank out the carry-forward and defaults applied below.
            const savedValues = valueModifier(defaultRequestDetail.value || {})
            let currentStepValues = {
                ...(docStep ? valueModifier(docStep.formValues || {}) : {}),
                ...Object.fromEntries(Object.entries(savedValues).filter(([, value]) => !isValueEmpty(value))),
            }

            if (defaultRequestDetail.currentCycle > 1) {
                let prevCycle = Number(defaultRequestDetail.currentCycle) - 2
                let reqWorkflow = defaultRequestDetail?.logs[prevCycle]
                let previousCycleStep = reqWorkflow?.find(step => step.name === defaultRequestDetail.status)
                if (previousCycleStep && previousCycleStep.formValues) {
                    // The previous cycle's snapshot of this step also holds every field it
                    // showed, including ones earlier steps own. Carry forward only what this
                    // cycle hasn't filled in yet, so the resubmitted values and anything
                    // already saved on this step aren't reverted to the old cycle's.
                    const currentCycleValues = defaultRequestDetail.value || {}
                    const carriedValues = Object.fromEntries(
                        Object.entries(previousCycleStep.formValues).filter(([key]) => isValueEmpty(currentCycleValues[key]))
                    )
                    currentStepValues = { ...valueModifier(carriedValues), ...currentStepValues }
                }
            }
            if (currentActiveForm?.defaultValues) {
                currentStepValues = { ...currentActiveForm.defaultValues, ...currentStepValues }
            }
            // This effect re-runs whenever the request object changes, which would
            // otherwise throw away edits the viewer has made but not yet saved;
            // `currentValues` is the live form store, so it is re-applied last.
            currentStepValues = { ...currentStepValues, ...currentValues }
            form.setFieldsValue({ ...currentStepValues })

            // System-level statuses that don't correspond to a workflow node
            const systemStatuses = ["deleted", "archived", "rejected", "approved", "draft"]
            // Get workflow nodes from the correct location
            const workflowNodes = workflowData?.node || requestDetail?.originalWorkflow?.nodes || []

            // Case-insensitive match for status -> node name
            let reqNode = workflowNodes.find(node => (node?.data?.name || "").toLowerCase() === (requestDetail?.status || "").toLowerCase())

            // Fallback: if no node matches current status, try last workflow step's name
            if (!reqNode && docStep?.name) {
                reqNode = workflowNodes.find(node => (node?.data?.name || "").toLowerCase() === (docStep.name || "").toLowerCase())
            }

            let curStep = {}
            if (reqNode && docStep) {
                curStep = { ...reqNode.data, ...docStep, id: reqNode.id }
            } else if (docStep) {
                // Use the logged workflow step only (already contains needed metadata)
                curStep = { ...docStep }
            } else if (reqNode) {
                curStep = { ...reqNode.data, id: reqNode.id }
            }

            // If current status is a system status with no matching node, don't show approval actions
            if (systemStatuses.includes((requestDetail?.status || "").toLowerCase()) && !reqNode) {
                setCurrentStep({})
                setCurrentAssignee(false)
                return
            }
            setCurrentStep(curStep)

            let assignee = isUserApprover(userInfo, defaultRequestDetail?.approvers, userGroups)

            if (assignee && hasUserActedOnCurrentStep(defaultRequestDetail, userInfo)) {
                assignee = false
            }

            setCurrentAssignee(!!assignee);
        }
    }, [defaultRequestDetail, workflowData, userGroups, requestDetail, userInfo])

    const getIsArchiveMappingFromItems = (items = []) => {
        const stack = Array.isArray(items) ? [...items] : []

        while (stack.length > 0) {
            const item = stack.shift()
            if (!item || typeof item !== "object") {
                continue
            }

            if (Array.isArray(item.children) && item.children.length > 0) {
                stack.push(...item.children)
            }

            if (item.formType !== "tableForm" || !Array.isArray(item.columns)) {
                continue
            }

            const archiveColumn = item.columns.find((column) => column?.isArchive === true)
            if (!archiveColumn) {
                continue
            }

            // Get parentTable from the archive column itself, or from a sibling master data column
            let dataId = archiveColumn?.parentTable || ""
            if (!dataId) {
                const masterDataColumn = item.columns.find((column) => column?.parentTable)
                dataId = masterDataColumn?.parentTable || ""
            }

            return {
                dataId,
                columnId: archiveColumn?.id || "",
            }
        }

        return null
    }

    const getIsArchiveMapping = () => {
        const workflowForms = Array.isArray(requestDetail?.workflowForms)
            ? requestDetail.workflowForms
            : (Array.isArray(defaultRequestDetail?.workflowForms) ? defaultRequestDetail.workflowForms : [])

        const activeForms = Array.isArray(rest.forms) ? rest.forms : []

        const activeWorkflowForm = currentActiveForm?.formId
            ? workflowForms.find((form) => form?.formId === currentActiveForm.formId)
            : null

        const formsToScan = [currentActiveForm, activeWorkflowForm, ...activeForms, ...workflowForms].filter((form, index, list) => {
            if (!form) {
                return false
            }

            if (form?.formId) {
                return index === list.findIndex((item) => item?.formId === form.formId)
            }

            return Boolean(form?.items)
        })

        for (const form of formsToScan) {
            const archiveMapping = getIsArchiveMappingFromItems(form?.items)
            if (archiveMapping) {
                return archiveMapping
            }
        }

        return null
    }

    const hasValidShareTo = (shareTo) => {
        if (Array.isArray(shareTo)) {
            return shareTo.some((fieldId) => typeof fieldId === "string" && fieldId.trim() !== "")
        }
        return typeof shareTo === "string" && shareTo.trim() !== ""
    }

    // Whether any step completed before the current one shared the request
    // externally. `shareTo` is only stored on the original workflow node, so each
    // executed step is resolved back to its node to read it.
    const previousStepsHaveShareTo = () => {
        const workflowNodes = requestDetail?.originalWorkflow?.nodes || workflowData?.node || []
        const executedSteps = Array.isArray(requestDetail?.workflow) ? requestDetail.workflow : []
        const currentStepIndex = executedSteps.findIndex((step) => step.name === currentStep?.name)
        // No match means the current step isn't in the executed path yet, so every
        // logged step precedes it.
        const previousSteps = currentStepIndex === -1 ? executedSteps : executedSteps.slice(0, currentStepIndex)

        return previousSteps.some((step) => {
            const stepNode = workflowNodes.find((node) => (node?.data?.name || "").toLowerCase() === (step?.name || "").toLowerCase())
            return hasValidShareTo(stepNode?.data?.shareTo)
        })
    }

    const submitRequest = async (status = "approve") => {
        let refusalStep = requestDetail.workflow.find(step => step.name === refusalFormValues.returnStep)

        // useWatch can miss setFieldValue writes (dropdowns, pickers). Read the
        // live store so next-approver conditions see what is on the form now.
        const liveFormValues = form.getFieldsValue(true) || {}
        const valuesForApprovers = {
            ...(requestDetail.value || {}),
            ...(currentValues || {}),
            ...(formValues || {}),
            ...liveFormValues,
        }

        let requestValues = { ...valuesForApprovers };
        let baseValue = formatPostValue(requestValues);
        baseValue = trimStringValues(baseValue);

        let postData = {
            ...requestDetail,
            value: (status === "approve" || refusalFormValues.refusalType === "definitiveRefusal")
                ? baseValue
                : trimStringValues({ ...requestDetail.value, ...currentValues, ...refusalStep?.formValues })
        }

        let nextStep = null, activityLogs = [...requestDetail.activityLogs], workflow = [...requestDetail.workflow], nextNotifyTo = { user: [], group: [], role: [], userPicker: [] }, nextApprovers = { user: [], group: [], role: [], userPicker: [] };
        let skippedSteps = [];
        let isApproved = false, approvalStatus = 'pending';
        let statuses = []
        let groupMembersMapping = {}

        if (currentStep) {
            statuses = currentStep?.statuses || []
            let approvers = getApproversWithDelegated(requestDetail.approvers, workflow, requestDetail.status)
            let currentUserGroups = []

            if (approvers.group?.length > 0) {
                let groupList = approvers.group
                for (let groupIndex = 0; groupIndex < groupList.length; groupIndex++) {
                    const groupId = groupList[groupIndex];
                    let groupInSpace = spaceSpecificGroups.find(grp => grp.groupId === groupId)
                    let members = []
                    if (groupInSpace?.users) {
                        members = groupInSpace.users.map(user => {
                            return user.email
                        })
                    }
                    // await getGroupMembers(groupName, userInfo)
                    groupMembersMapping = {
                        ...groupMembersMapping,
                        [groupId]: Array.from(new Set(members))
                    }
                }
            }
            for (const key in groupMembersMapping) {
                if (groupMembersMapping[key].includes(userInfo.email)) {
                    currentUserGroups.push(key)
                }
            }

            // Each entry carries its own action time. Without it the step history has
            // nothing per-entry to render and falls back to whenever the request row
            // was last written, so every step shows the same date once a later step
            // is actioned.
            let newStatus = []

            if (approvers?.user?.includes(userInfo.email) && approvers.group?.length > 0) {
                // A user approving both as a named approver and as a group member should
                // still only get one "group" status entry, even if they belong to more
                // than one of the step's required groups.
                if (currentUserGroups.length > 0) {
                    newStatus.push({
                        approvalStatus: status == "submit" ? "submitted" : status == "approve" ? "approved" : "refused",
                        actionBy: userInfo.email,
                        updatedOn: moment().utc(),
                        comment: refusalFormValues?.comment || "",
                        approverType: "group",
                        approverValue: currentUserGroups[0]
                    })
                }
                newStatus.push({
                    approvalStatus: status == "submit" ? "submitted" : status === "approve" ? "approved" : "refused",
                    actionBy: userInfo.email,
                    updatedOn: moment().utc(),
                    comment: refusalFormValues?.comment || "",
                    approverType: "user",
                    approverValue: userInfo.email
                })

            } else if (currentUserGroups?.length > 0) {
                // Push a single status even when the user belongs to multiple required
                // groups; group consensus below matches by membership, not approverValue,
                // so this one entry still counts toward every group they belong to.
                newStatus.push({
                    approvalStatus: status == "submit" ? "submitted" : status == "approve" ? "approved" : "refused",
                    actionBy: userInfo.email,
                    updatedOn: moment().utc(),
                    comment: refusalFormValues?.comment || "",
                    approverType: "group",
                    approverValue: currentUserGroups[0]
                })
            } else {
                newStatus.push({
                    approvalStatus: status == "submit" ? "submitted" : status === "approve" ? "approved" : "refused",
                    actionBy: userInfo.email,
                    updatedOn: moment().utc(),
                    comment: refusalFormValues?.comment || "",
                    approverType: "user",
                    approverValue: userInfo.email
                })
            }

            if (statuses) {
                statuses = [...statuses, ...newStatus]
            } else {
                statuses = [...newStatus]
            }
            let approverTypes = Object.keys(approvers)

            if (currentStep?.assignmentType === "ONE") {
                let found = statuses.filter(s => (s.approvalStatus == "approved" || s.approvalStatus === "submitted"))
                let approvals = found ? found.filter(f => f.approvalStatus == "approved" || f.approvalStatus === "submitted") : []
                let refusals = found ? found.filter(f => f.approvalStatus == "refused") : []
                if (approvals && approvals.length > 0) {
                    approvalStatus = "approved"
                } else if (refusals && refusals.length > 0) {
                    approvalStatus = "refused"
                }
            } else if (currentStep?.assignmentType === "MULTIPLE") {
                let userTypeStatus = {}

                for (let index = 0; index < approverTypes.length; index++) {
                    const approverType = approverTypes[index];
                    let found = statuses.filter(s => (s.approverType == approverType))
                    let statusToBeCompared = status === "submit" ? "submitted" : "approved"

                    if (found.length > 0) {
                        let approvedByAdmin = found.find(f => f.approvalStatus == statusToBeCompared && f.actionBy == userInfo.email && userInfo.isAdmin)
                        if (approvedByAdmin) {
                            approvalStatus = "approved"
                            userTypeStatus = { user: "approved", group: "approved", role: "approved", userPicker: "approved" }
                            break
                        }
                        let refusedByAdmin = found.find(f => f.approvalStatus == "refused" && (f.actionBy == userInfo.email))
                        if (refusedByAdmin) {
                            approvalStatus = "refused"
                            userTypeStatus = { user: "refused", group: "refused", role: "refused", userPicker: "refused" }
                            break
                        }
                    }
                    let approvals = found ? found.filter(f => f.approvalStatus == statusToBeCompared) : []
                    let refusals = found ? found.filter(f => f.approvalStatus == "refused") : []
                    userTypeStatus[approverType] = approvers[approverType]?.length == 0 ? "approved" : "pending"

                    if (approverType == "user" && approvals && approvals.length > 0 && approvals.length == approvers[approverType].length) {

                        userTypeStatus[approverType] = "approved"
                    } else if (approverType == "user" && refusals && refusals.length > 0) {
                        // approvalStatus = "refused"
                        userTypeStatus[approverType] = "refused"
                        //break;
                    } else if (approverType == "userPicker") {
                        userTypeStatus[approverType] = "approved"
                    } else if (approverType == "group") {
                        let groups = approvers.group
                        let groupStatus = {}
                        let allGroupsApproved = true
                        for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
                            const group = groups[groupIndex];
                            let found = statuses.filter(s => s.approverType == "group")
                            // let found = statuses.filter((s: any) => s.group == group)

                            if (found && found.length == 0) {
                                groupStatus[group] = "pending"
                            } else if (found) {
                                let approvals = found ? found.filter(f => f.approvalStatus == "approved" && groupMembersMapping[group]?.includes(f.actionBy)) : []
                                // && groupMembersMapping[group].includes(f.approvedBy)
                                let refusals = found ? found.filter(f => f.approvalStatus == "refused") : []
                                if (approvals && groupMembersMapping[group] && approvals.length == groupMembersMapping[group]?.length) {
                                    groupStatus[group] = "approved"
                                } else if (refusals && groupMembersMapping[group] && refusals.length > 0 && refusals.length == groupMembersMapping[group].length) {
                                    groupStatus[group] = "refused"
                                    allGroupsApproved = false
                                } else if (refusals && refusals.length > 0 && approvals && approvals.length > 0) {
                                    groupStatus[group] = "refused"
                                    allGroupsApproved = false
                                } else {
                                    groupStatus[group] = "pending"
                                }
                            }
                        }
                        for (let groupStatusIndex = 0; groupStatusIndex < Object.keys(groupStatus).length; groupStatusIndex++) {
                            const group = Object.keys(groupStatus)[groupStatusIndex];
                            if (groupStatus[group] == "pending") {
                                allGroupsApproved = false
                                break
                            }
                        }
                        userTypeStatus[approverType] = allGroupsApproved ? "approved" : "pending"
                    } else if (approverType === "role") {
                        userTypeStatus[approverType] = "approved"
                    }
                }

                let allApproved = true
                let allRefused = false
                let userTypeStatusList = Object.keys(userTypeStatus)
                for (let index = 0; index < userTypeStatusList.length; index++) {
                    const userType = userTypeStatusList[index];
                    if (userTypeStatus[userType] == "pending") {
                        allApproved = false
                    }
                    if (userTypeStatus[userType] == "refused") {
                        allApproved = false
                        allRefused = true
                        break;
                    }
                }
                if (allRefused) {
                    approvalStatus = "refused"
                } else if (allApproved) {
                    approvalStatus = "approved"
                }
            } else if (currentStep?.assignmentType === "ONE_PER_GROUP") {
                let userTypeStatus = {}

                for (let index = 0; index < approverTypes.length; index++) {
                    const approverType = approverTypes[index];
                    let found = statuses.filter(s => (s.approverType == approverType))
                    let statusToBeCompared = status === "submit" ? "submitted" : "approved"

                    if (found.length > 0) {
                        let approvedByAdmin = found.find(f => f.approvalStatus == statusToBeCompared && f.actionBy == userInfo.email && userInfo.isAdmin)
                        if (approvedByAdmin) {
                            approvalStatus = "approved"
                            userTypeStatus = { user: "approved", group: "approved", role: "approved", userPicker: "approved" }
                            break
                        }
                        let refusedByAdmin = found.find(f => f.approvalStatus == "refused" && (f.actionBy == userInfo.email))
                        if (refusedByAdmin) {
                            approvalStatus = "refused"
                            userTypeStatus = { user: "refused", group: "refused", role: "refused", userPicker: "refused" }
                            break
                        }
                    }
                    if (approvers[approverType]?.length === 0) {
                        userTypeStatus[approverType] = status === "submit" || status === "approve" ? "approved" : "refused"
                        continue
                    }
                    let approvals = found ? found.filter(f => f.approvalStatus == statusToBeCompared) : []
                    let refusals = found ? found.filter(f => f.approvalStatus == "refused") : []
                    userTypeStatus[approverType] = approvers[approverType]?.length == 0 ? "approved" : "pending"

                    if (approverType == "user" && approvals && approvals.length > 0 && approvals.length == approvers[approverType].length) {

                        userTypeStatus[approverType] = "approved"
                    } else if (approverType == "user" && refusals && refusals.length > 0) {
                        // approvalStatus = "refused"
                        userTypeStatus[approverType] = "refused"
                        //break;
                    } else if (approverType == "userPicker") {
                        userTypeStatus[approverType] = "approved"
                    } else if (approverType == "group") {
                        let groups = approvers.group
                        let groupStatus = {}
                        let allGroupsApproved = true
                        for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
                            const group = groups[groupIndex];
                            let found = statuses.filter(s => s.approverType == "group")

                            if (found && found.length == 0) {
                                groupStatus[group] = "pending"
                            } else if (found) {
                                let approvals = found ? found.filter(f => f.approvalStatus == statusToBeCompared && groupMembersMapping[group]?.includes(f.actionBy)) : []
                                let refusals = found ? found.filter(f => f.approvalStatus == "refused") : []
                                if (approvals && approvals.length > 0) {
                                    groupStatus[group] = "approved"
                                } else if (refusals && groupMembersMapping[group] && refusals.length > 0) {
                                    groupStatus[group] = "refused"
                                    allGroupsApproved = false
                                } else {
                                    groupStatus[group] = "pending"
                                }
                            }
                        }
                        for (let groupStatusIndex = 0; groupStatusIndex < Object.keys(groupStatus).length; groupStatusIndex++) {
                            const group = Object.keys(groupStatus)[groupStatusIndex];
                            if (groupStatus[group] == "pending") {
                                allGroupsApproved = false
                                break
                            }
                        }
                        userTypeStatus[approverType] = allGroupsApproved ? "approved" : "pending"
                    } else if (approverType === "role") {
                        userTypeStatus[approverType] = "approved"
                    }
                }

                let allApproved = true
                let allRefused = false
                let userTypeStatusList = Object.keys(userTypeStatus)
                for (let index = 0; index < userTypeStatusList.length; index++) {
                    const userType = userTypeStatusList[index];
                    if (userTypeStatus[userType] == "pending") {
                        allApproved = false
                    }
                    if (userTypeStatus[userType] == "refused") {
                        allApproved = false
                        allRefused = true
                        break;
                    }
                }
                if (allRefused) {
                    approvalStatus = "refused"
                } else if (allApproved) {
                    approvalStatus = "approved"
                }
            }
        }
        // For approval step, only track changes in the current active form
        let formItems = []
        rest.forms.map(form => {
            if (form.items) {
                formItems = formItems.concat(form.items)
            }
        })
        if (currentActiveForm?.items) {
            formItems = formItems.concat(currentActiveForm.items)
        }
        let formWidgetList = getVisibleWidgets(formItems, { ...formValues }, userInfo, userGroups, currentStatus, conditions)

        if (status === "approve" || status === "submit") {

            if (approvalStatus === "approved") {
                // Get workflow edges from the correct location
                const workflowEdges = requestDetail?.originalWorkflow?.edges || []
                let sortedNodes = sortNodesByEdges(requestDetail?.originalWorkflow?.nodes || [], workflowEdges)

                let currentStepEdge = workflowEdges.find(edge => edge.source === currentStep.id);
                if (currentStepEdge) {
                    let currentStepIndex = sortedNodes.findIndex(node => node.id === currentStepEdge.target)
                    let nextNodes = sortedNodes.filter((node, index) => index >= currentStepIndex)
                    let nextStepNode = {}
                    let foundNextStep = false
                    // nextNodes.find(node => node.id === currentStepEdge.target)
                    for (const node of nextNodes) {
                        nextStepNode = { ...node }
                        if (node.type !== "textInput") {
                            continue;
                        }
                        nextApprovers = { user: [], group: [], role: [], userPicker: [] }
                        if (nextStepNode.data.approverType === "default") {
                            nextApprovers = getRequestApprovers(nextStepNode.data.assignTo, valuesForApprovers, spaceSpecificUsers, spaceSpecificGroups)
                        } else if (nextStepNode.data?.approverType === "conditional") {
                            let conditions = workflowConditions.filter(condition => condition.nodeId === node.id)
                            if (conditions.length > 0) {
                                let conditionStatus = nodeConditionsHandler(conditions, valuesForApprovers, formItems)
                                if (conditionStatus?.length > 0) {
                                    conditionStatus.map(condition => {
                                        let curApprovers = getRequestApprovers(condition.approvers, valuesForApprovers, spaceSpecificUsers, spaceSpecificGroups)
                                        for (const key in curApprovers) {
                                            nextApprovers[key] = Array.from(new Set([...nextApprovers[key], ...curApprovers[key]]))
                                        }
                                    })
                                }
                            }
                        }
                        if (isObjectEmpty(nextApprovers, ["user", "group"])) {
                            // No resolvable approvers: bypass the step but keep a
                            // record of it so the timeline shows it was skipped.
                            skippedSteps.push(buildSkippedStepData(nextStepNode))
                            continue;
                        } else {
                            foundNextStep = true
                            break;
                        }
                    }

                    if (foundNextStep && nextStepNode?.type === "textInput") {
                        nextStep = nextStepNode
                    } else {
                        const endNode = nextNodes.find(node => node?.data?.type === "end")
                            || sortedNodes.find(node => node?.data?.type === "end")
                        if (endNode) {
                            isApproved = true
                            nextStep = { ...endNode }
                            nextApprovers = { user: [], group: [], role: [], userPicker: [] }
                        }
                    }
                }

                if (nextStep && !isApproved) {
                    postData.dueDate = getDueDate(nextStep, requestDetail)
                    postData.status = nextStep.data?.name
                    if (nextStep.data.approverType === "default") {
                        nextApprovers = getRequestApprovers(nextStep.data.assignTo, valuesForApprovers, spaceSpecificUsers, spaceSpecificGroups)
                    } else if (nextStep.data?.approverType === "conditional") {
                        let conditions = workflowConditions.filter(condition => condition.nodeId === nextStep.id)
                        if (conditions.length > 0) {
                            let conditionStatus = nodeConditionsHandler(conditions, valuesForApprovers, formItems)
                            if (conditionStatus?.length > 0) {
                                conditionStatus.map(condition => {
                                    let curApprovers = getRequestApprovers(condition.approvers, valuesForApprovers, spaceSpecificUsers, spaceSpecificGroups)
                                    for (const key in curApprovers) {
                                        nextApprovers[key] = Array.from(new Set([...nextApprovers[key], ...curApprovers[key]]))
                                    }
                                })
                            }
                        }
                    }
                    postData.approvers = nextApprovers
                    if (nextStep?.data?.notifyTo) {
                        nextNotifyTo = getRequestApprovers(nextStep?.data.notifyTo, valuesForApprovers, spaceSpecificUsers, spaceSpecificGroups)
                    }
                } else if (isApproved) {
                    let endNode = workflowData.node.find(node => node?.data?.type === "end")
                    postData.status = endNode?.data?.name || "approved"
                    postData.approvers = { user: [], group: [], role: [], userPicker: [] }
                    postData.dueDate = ""
                }
            } else if (approvalStatus === "pending") {
                postData.status = requestDetail.status
            }

            let currentStepPreviousValues = requestDetail?.value || {}
            const combinedValues = { ...currentStepPreviousValues, ...currentValues, ...formValues }
            let modCurrentValues = getDifferenceValues(formItems, combinedValues, userInfo, userGroups, currentStatus, conditions, currentStepPreviousValues)
            const stepCaptured = getStepFormValues(formItems, combinedValues, userInfo, userGroups, currentStatus, conditions, formItems)
            postData.value = { ...requestDetail.value, ...modCurrentValues }
            workflow = workflow.map(step => {
                if (step.name === currentStep.name) {
                    return { ...step, statuses: statuses, approvers: postData.approvers, formValues: { ...stepCaptured }, approvalStatus: currentStep?.approvalStep ? approvalStatus : "submitted", comment: refusalFormValues?.comment || "", actionBy: userInfo.email, actionOn: moment().utc(), delayed: approvalDelayed(requestDetail.dueDate) }
                }
                return { ...step }
            })
            let reqStepData = {}
            if (approvalStatus === "approved") {
                if (!isApproved) {
                    reqStepData = {
                        formValues: {},
                        name: nextStep?.data?.name,
                        count: nextStep?.data?.count,
                        approvalStep: nextStep.data?.approvalStep,
                        period: nextStep.data?.period,
                        assignTo: nextApprovers,
                        notifyTo: nextNotifyTo,
                        formId: nextStep.formData?.formId,
                        approvalStatus: "pending",
                        dueDate: getDueDate(nextStep, requestDetail),
                        comment: "",
                        actionBy: "",
                        actionOn: "",
                        delayed: false,
                        // collaborators: currentStep?.collaborators || {user: []},
                        // delegatedUser: []
                        // getDelegatedUser(userInfo, postData.approvers)
                    }
                    if(currentStep.collaborators) {
                        reqStepData.collaborators = currentStep.collaborators
                    }
                }
                else {
                    let endNode = workflowData.node.find(node => node?.data?.type === "end")
                    reqStepData = { ...endNode?.data, approvalStatus: "approved" }
                    postData.status = endNode?.data?.name || "approved"
                    postData.approvers = { user: [], group: [], role: [], userPicker: [] }
                }
                // Steps bypassed on the way to this one belong between the step
                // just actioned and the step the request moves to.
                workflow = workflow.concat([...skippedSteps])
                workflow.push(reqStepData)
            }

        }
        let statusText = status === "submit" ? "submitted" : `${status}d` 
        let activityMessage = `${userInfo.name} has ${statusText} the ${requestDetail.status} step`

        if (status === "reject") {
            let requestValues = {}

            if (refusalFormValues.refusalType === "returnToStep") {
                const workflowNodes = workflowData?.node || requestDetail?.originalWorkflow?.nodes || []
                let nextStep = workflowNodes.find(node => node.data.name === refusalStep.name)
                let stepApprovers = getRequestApprovers(nextStep?.data?.assignTo || {}, valuesForApprovers, spaceSpecificUsers, spaceSpecificGroups)
                postData.currentCycle = Number(requestDetail.currentCycle) + 1
                postData.status = refusalStep.name;
                postData.approvers = stepApprovers
                postData.dueDate = refusalStep?.deadline ? "" : moment().add(refusalStep.count, 'day').format("YYYY-MM-DD")
                activityMessage = `${userInfo.name} has rejected the ${requestDetail.status} step and sent back to the ${postData.status} step`

                let curStepData = {
                    ...currentStep,
                    statuses: statuses,
                    formValues: currentStep.formValues ? { ...currentStep.formValues } : {},
                    approvalStatus: "rejected",
                    comment: refusalFormValues?.comment || "",
                    actionBy: userInfo.email,
                    actionOn: moment().utc(),
                    delayed: approvalDelayed(requestDetail.dueDate)
                }
                workflow = workflow.map(step => {
                    if (step.name === currentStep.name) {
                        return { ...curStepData, assignTo: stepApprovers }
                    }
                    return { ...step }
                })
                let logsWorkflow = [...workflow]
                let logsIndex = requestDetail.logs ? Object.keys(requestDetail.logs).length : 0
                let currentLogs = requestDetail.logs ? { ...requestDetail.logs } : {}
                postData.logs = { ...currentLogs, [logsIndex]: [...logsWorkflow] }
                let indexOfBackToStep = requestDetail.workflow.findIndex(step => step.name === refusalStep.name)
                if (indexOfBackToStep !== -1) {
                    workflow = requestDetail.workflow.filter((step, index) => index <= indexOfBackToStep)
                    if (indexOfBackToStep === 0) {
                        requestValues = workflow[indexOfBackToStep].formValues
                        workflow = []
                    } else {
                        for (let stepIndex = 0; stepIndex <= indexOfBackToStep; stepIndex++) {
                            requestValues = workflow[stepIndex].formValues ? { ...requestValues, ...workflow[stepIndex].formValues } : { ...requestValues }
                        }
                        workflow = workflow.map(step => {
                            if (step.name === refusalStep.name) {
                                let stepDetail = { ...step, actionBy: "", actionOn: "", dueDate: getDueDate(refusalStep, requestDetail), comment: "", approvalStatus: "pending", statuses: [] }
                                if (indexOfBackToStep === 0) {
                                    stepDetail.submitted = false
                                    stepDetail.dueDate = ""
                                }
                                stepDetail.formValues = logsWorkflow[indexOfBackToStep].formValues || {}
                                return { ...stepDetail }
                            }
                            return { ...step }
                        })
                    }
                }
            } else {
                postData.status = "rejected"
                postData.approvers = { user: [], group: [], role: [], userPicker: [] }
                postData.dueDate = ""
                activityMessage = `${userInfo.name} has rejected the request at ${requestDetail.status}`

                let reqStepData = {
                    ...currentStep,
                    statuses: statuses,
                    formValues: {},
                    approvalStatus: "rejected",
                    dueDate: "",
                    comment: refusalFormValues?.comment || "",
                    actionBy: userInfo.email,
                    actionOn: moment().utc(),
                    delayed: approvalDelayed(requestDetail.dueDate)
                }
                workflow = workflow.map(step => {
                    if (step.name === currentStep.name) {
                        return { ...reqStepData }
                    }
                    return { ...step }
                })
                requestValues = { ...postData.value }

                // workflow = workflow.concat({...reqStepData })
            }
            postData.value = { ...requestValues }
        }

        // External users only learn about a rejection if an earlier step shared the
        // request with them; the backend keys that mail off this flag. Cleared
        // otherwise so a value echoed back on `requestDetail` can't leak through.
        if (status === "reject" && previousStepsHaveShareTo()) {
            postData.notifyExternalUserForRejection = true
        } else {
            delete postData.notifyExternalUserForRejection
        }

        activityLogs.push({ "message": activityMessage, updatedOn: moment().utc(), updatedBy: userInfo.email })
        postData.activityLogs = activityLogs
        postData["workflow"] = workflow
        if (postData.outlookEventData) {
            delete postData.outlookEventData
        }
        const isArchiveMapping = getIsArchiveMapping()
        if (isArchiveMapping) {
            postData.isArchive = isArchiveMapping
        } else {
            delete postData.isArchive
        }
        // When the current form being submitted contains scoring-enabled fields
        // and the request hasn't been scored yet, flag the backend to calculate
        // the assessment score (never for drafts). Upcoming forms are ignored.
        const hasScoringField = getAllWidgets(currentActiveForm?.items || []).some((widget) => widget.enableScoring)
        if (postData.status !== "draft" && hasScoringField && !defaultRequestDetail.assessmentScore) {
            postData.calculateScore = true
        }
        // return
        showLoader(true)
        let updateResponse = await submitRequestForm(postData)
        showLoader(false)
        if (updateResponse.status === 200) {
            setSubmitMessage(t(updateResponse.message))
            setMessageState(true)
            let companyIdentifier = getStoredCompanyIdentifier()
            let modInformation = {
                ...postData.value,
                "Recipient": "[Recipient]",
                "Chatroom name": "",
                "Chatroom message sender": "",
                "Space name": spaceData?.name,
                "Request Id": requestDetail.caseId,
                spaceId: rest.spaceId,
                redirectUrl: `/${companyIdentifier}/app/space/${rest.spaceId}/approve/${rest.workflowId}/${postData.requestId}`,
                "Last comment": refusalFormValues?.comment ? refusalFormValues.comment?.replaceAll("<", "&lt;").replaceAll(">", "&gt;") : "",
                "Last updater": userInfo.name,
                "Due date": postData.dueDate,
                "Workflow step": postData?.status,
                "Current step": postData?.status
            }
            if (requestDetail?.status !== postData.status) {
                let defaultNodeLabel = workflowData?.node?.find(step => step.type === "default")?.data?.name || ""
                let curStep = workflowData?.node?.find(step => step?.data?.name === requestDetail.status)
                let reqTemplate = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_WAITING")
                if (postData.dueDate) {
                    reqTemplate = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_WAITING_WITH_DUE_DATE")
                }
                let notificationTemplateForNotified = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_NOTIFICATION")

                let approvedStatusLabel = postData.originalWorkflow?.nodes?.find(node => node.data?.type === "end")?.data?.name || ""
                // TRIGGER END NOTIFICATION
                if (postData.status === approvedStatusLabel) {
                    reqTemplate = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_END_NOTIFICATION")
                    let endNode = workflowData?.node?.find(step => step?.data?.type === "end")
                    let approvedNotifiedUsers = getRequestApprovers(endNode?.data?.notifyTo || {}, postData.value, spaceSpecificUsers, spaceSpecificGroups)

                    if (reqTemplate?.enabled) {
                        triggerNotification({
                            recipients: { user: approvedNotifiedUsers?.user ? [...approvedNotifiedUsers.user] : [], group: approvedNotifiedUsers?.group ? [...approvedNotifiedUsers.group] : [] },
                            notificationParams: modInformation,
                            template: reqTemplate,
                            userInfo: userInfo,
                            currentStepInfo: nextStep,
                            spaceSpecificGroups: spaceSpecificGroups,
                            spaceSpecificUsers: spaceSpecificUsers,
                            formItems: formWidgetList
                        })
                    }
                }
                // TRIGGER REFUSAL NOTIFICATION
                else if (refusalFormValues?.comment) {

                    // DEFINITIVE REFUSAL
                    if (refusalFormValues?.refusalType) {
                        reqTemplate = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_REFUSAL")
                        if (reqTemplate?.enabled) {
                            let curStep = workflowData?.node?.find(step => requestDetail.status === step?.data?.name)
                            let curStepNotifiedUsers = getRequestApprovers(curStep?.data?.notifyTo || {}, postData.value, spaceSpecificUsers, spaceSpecificGroups)
                            if (refusalFormValues?.refusalType === "definitiveRefusal") {
                                curStepNotifiedUsers.user = curStepNotifiedUsers.user.concat(postData?.createdBy?.email)
                            }
                            triggerNotification({
                                recipients: { user: curStepNotifiedUsers?.user ? [...curStepNotifiedUsers.user] : [], group: curStepNotifiedUsers?.group ? [...curStepNotifiedUsers.group] : [] },
                                notificationParams: { ...modInformation, "Workflow step": requestDetail?.status },
                                template: reqTemplate,
                                userInfo: userInfo,
                                currentStepInfo: null,
                                spaceSpecificGroups: spaceSpecificGroups,
                                spaceSpecificUsers: spaceSpecificUsers,
                                formItems: formWidgetList
                            })
                        }
                    }
                    // REFUSE AND RETURN TO
                    if (refusalFormValues.refusalType === "returnToStep") {
                        let sendBackTemplate = notificationTemplates.find(notification => notification.notificationType === "WORKFLOW_STEP_SENT_BACK")
                        let approvers = { user: [], group: [] }, notifiedUsers = null

                        if (defaultNodeLabel === refusalFormValues.returnStep) {
                            approvers = { user: [postData.createdBy?.email], group: [] }
                        } else {
                            approvers = getUniqueStepUsers(postData.approvers, spaceSpecificGroups)
                            notifiedUsers = getRequestApprovers(nextStep?.data?.notifyTo || {}, postData.value, spaceSpecificUsers, spaceSpecificGroups)
                        }
                        const sentBackParams = {
                            ...modInformation,
                            "Workflow step": curStep?.data?.name || requestDetail?.status,
                            "Current step": postData.status,
                        }
                        if (sendBackTemplate?.enabled) {
                            triggerNotification({
                                recipients: approvers,
                                notificationParams: sentBackParams,
                                template: sendBackTemplate,
                                userInfo: userInfo,
                                currentStepInfo: nextStep,
                                spaceSpecificGroups: spaceSpecificGroups,
                                spaceSpecificUsers: spaceSpecificUsers,
                                formItems: formWidgetList
                            })
                        }
                        if (notifiedUsers && sendBackTemplate?.enabled) {
                            triggerNotification({
                                recipients: notifiedUsers,
                                notificationParams: sentBackParams,
                                template: sendBackTemplate,
                                userInfo: userInfo,
                                currentStepInfo: nextStep,
                                spaceSpecificGroups: spaceSpecificGroups,
                                spaceSpecificUsers: spaceSpecificUsers,
                                formItems: formWidgetList
                            })
                        }

                    } else {
                        if (reqTemplate?.enabled && canSendStepWaitingNotification(reqTemplate, nextStep)) {
                            let uniqueApprovers = getUniqueStepUsers(postData.approvers, spaceSpecificGroups)
                            triggerNotification({
                                recipients: uniqueApprovers,
                                notificationParams: { ...modInformation },
                                template: reqTemplate,
                                userInfo: userInfo,
                                currentStepInfo: nextStep,
                                spaceSpecificGroups: spaceSpecificGroups,
                                spaceSpecificUsers: spaceSpecificUsers,
                                formItems: formWidgetList
                            })
                        }
                        let uniqueNotifiedUsers = getRequestApprovers(nextStep?.data?.notifyTo || {}, postData.value, spaceSpecificUsers, spaceSpecificGroups)
                        triggerNotification({
                            recipients: uniqueNotifiedUsers,
                            notificationParams: modInformation,
                            template: notificationTemplateForNotified,
                            userInfo: userInfo,
                            currentStepInfo: nextStep,
                            spaceSpecificGroups: spaceSpecificGroups,
                            spaceSpecificUsers: spaceSpecificUsers,
                            formItems: formWidgetList
                        })
                    }
                }
                else {
                    // The waiting mail only goes out when the step the request moved
                    // to is an approval step; a plain submit step has nothing to approve.
                    if (reqTemplate?.enabled && canSendStepWaitingNotification(reqTemplate, nextStep)) {
                        let uniqueApprovers = getUniqueStepUsers(postData.approvers, spaceSpecificGroups)
                        triggerNotification({
                            recipients: uniqueApprovers,
                            notificationParams: modInformation,
                            template: reqTemplate,
                            userInfo: userInfo,
                            currentStepInfo: nextStep,
                            spaceSpecificGroups: spaceSpecificGroups,
                            spaceSpecificUsers: spaceSpecificUsers,
                            formItems: formWidgetList
                        })
                    }
                    if (notificationTemplateForNotified?.enabled) {
                        let nextNotifyTo = workflow[workflow.length - 1]?.notifyTo || { user: [], group: [] }
                        let uniqueNotifiedUsers = getRequestApprovers(nextNotifyTo, postData.value, spaceSpecificUsers, spaceSpecificGroups)
                        triggerNotification({
                            recipients: uniqueNotifiedUsers,
                            notificationParams: modInformation,
                            template: notificationTemplateForNotified,
                            userInfo: userInfo,
                            currentStepInfo: nextStep,
                            spaceSpecificGroups: spaceSpecificGroups,
                            spaceSpecificUsers: spaceSpecificUsers,
                            formItems: formWidgetList
                        })

                    }

                }
            }
        }
    }

    const submitForm = async () => {
        //  try {
        //     await requestForm.validateFields();

        // } catch (errorInfo) {
        //     message.error("Please fill all the mandatory fields");
        //     return
        // }
        try {
            await requestForm.validateFields();
        } catch (errorInfo) {
            message.error(t('Please fill all the mandatory fields'));
            scrollToFirstMissingField(requestForm, errorInfo, document.querySelector('.request-form') || document);
            return
        }
        try {
            await form.validateFields();
            openRefusalModal("approved")
        } catch (errorInfo) {
            message.error(t('Please fill all the mandatory fields'));
            scrollToFirstMissingField(form, errorInfo, approvalFormRef.current || document.querySelector('.request-details') || document);
        }
    }

    const openRefusalModal = async (type = "approved") => {
        setShowRefusalModal(true)
        if (type === "approved") {
            setIsApproving(true)
            return
        }
        setIsApproving(false)
        refusalForm.setFieldsValue({ refusalType: "returnToStep", returnToStep: undefined })
        let currentWorkflow = requestDetail.workflow.filter(step =>
            step.name !== currentStep.name && step.approvalStatus !== "skipped"
        )
        currentWorkflow = currentWorkflow.map(step => {
            return { label: step.name, value: step.name }
        })
        setReturnSteps(currentWorkflow)
    }
    const cancelRefusal = () => {
        setShowRefusalModal(false)
        refusalForm.resetFields();
    }

    const refuseStep = () => {
        // If approving, check for required attachments first
        // if (isApproving) {
        //     return;
        // }

        setShowRefusalModal(false)
        submitRequest(isApproving ? (currentStep?.approvalStep ? "approve" : "submit") : "reject")

    }

    const handleFormChange = (changedValues, allValues) => {
        if (rest.passFormValues) {
            const nextValues = allValues || form.getFieldsValue(true)
            rest.passFormValues({ ...nextValues })
        }
    }

    const handleShareLink = async () => {
        if (recipients.length === 0) {
            setErrorMessage("Please add recipients");
            return;
        }
        let emails = recipients.split(", ");
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        let invalidEmails = emails.filter(email => {
            return !emailPattern.test(email)
        })
        if (invalidEmails?.length > 0) {
            setErrorMessage("Invalid emails: " + invalidEmails.join(", "))
            return;
        }
        setErrorMessage("")
        let postData = { requestId: defaultRequestDetail.requestId, formId: currentActiveForm?.formId, emails: emails, language: shareLanguage, allowMultipleSubmissions: allowMultipleSubmissions, allowPreviousForms: allowPreviousForms }
        let sharedResponse = await shareOngoingPublicLink(postData)
        // await sharePublicLink(postData)
        if (sharedResponse?.status === 200) {
            message.success(sharedResponse.message)
            setShowShareModal(false)
            setRecipients("")
            setShareLanguage(DEFAULT_LANGUAGE)
            setAllowMultipleSubmissions(false)
            setAllowPreviousForms(false)
            setErrorMessage("")
        }

    }

    const handleCloseRecipientModal = () => {
        setShowShareModal(false)
        setRecipients("")
        setShareLanguage(DEFAULT_LANGUAGE)
        setAllowMultipleSubmissions(false)
        setAllowPreviousForms(false)
        setErrorMessage("")
    }

    const isSubmitOnlyStep = isApproving && !currentStep?.approvalStep

    const MAX_COLLABORATORS = 5
    const allowShareToExternalUser = spaceData?.allowShareToExternalUser === true || spaceData?.allowShareToExternalUser === "true"

    const existingCollaboratorsCount = currentStep.collaborators?.user?.length || 0
    const remainingCollaboratorSlots = Math.max(0, MAX_COLLABORATORS - existingCollaboratorsCount)

    const addCollaboratorField = () => {
        if (collaborators.length >= remainingCollaboratorSlots) return
        setCollaborators([...collaborators, { name: "", email: "" }])
        setCollaboratorErrors([...collaboratorErrors, { name: false, email: false }])
    }

    const removeCollaboratorField = (index) => {
        setCollaborators(collaborators.filter((_, i) => i !== index))
        setCollaboratorErrors(collaboratorErrors.filter((_, i) => i !== index))
    }

    const updateCollaboratorField = (index, field, value) => {
        setCollaborators(collaborators.map((item, i) => i === index ? { ...item, [field]: value } : item))
        setCollaboratorErrors(collaboratorErrors.map((err, i) => i === index ? { ...err, [field]: false } : err))
    }

    const handleAddCollaborator = async () => {
        if (collaborators.length > remainingCollaboratorSlots) {
            message.error(`You can only add ${remainingCollaboratorSlots} more collaborator${remainingCollaboratorSlots !== 1 ? "s" : ""} (max ${MAX_COLLABORATORS} total)`)
            return
        }
        const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        const errors = collaborators.map(c => ({
            name: !c.name.trim(),
            email: !c.email.trim() || !emailPattern.test(c.email.trim())
        }))
        setCollaboratorErrors(errors)
        const hasErrors = errors.some(e => e.name || e.email)
        if (hasErrors) {
            message.error(t('Please fill in all name and email fields with valid values'))
            return
        }
        let postData = { spaceId: defaultRequestDetail.spaceId, requestId: defaultRequestDetail.requestId, status: defaultRequestDetail.status, collaborators: collaborators.map(c => ({ username: c.name.trim(), email: c.email.trim() })) }
        showLoader(true)
        let postResponse = await addCollaboratorInSpace(postData)
        showLoader(false)
        if (postResponse.status === 200) {
            message.success(t('Collaborators added successfully'))
            setShowCollaboratorModal(false)
            setCollaborators([{ name: "", email: "" }])
            setCollaboratorErrors([])
            if (getRequestDetail) getRequestDetail()
        } else {
            message.error(postResponse?.message || "Failed to add collaborators")
            return
        }
    }

    return (
        <div ref={approvalFormRef}>
        <Flex gap={16} vertical className="approval-details">
            {currentStep.name && <WorkflowCard openDefault={true} data={currentStep} currentValues={currentValues} definedWidgets={definedWidgets} spaceSpecificGroups={spaceSpecificGroups} />}
            {currentActiveForm?.items ?
                <div className="approval-form" style={{ border: currentActiveForm?.items ? '1px solid #141BCB' : "0", padding: currentActiveForm?.items ? "1rem" : "0" }}>
                    <div className="flex justify-between items-center gap-3">
                        <div className="form-title">
                            {localizedFormHeader.label || "Fill Details"}
                        </div>
                        <Space>
                            {
                                allowShareToExternalUser ?
                                    <Button className="default-btn" size="sm"
                                        icon={<ShareAltOutlined />}
                                        onClick={() => setShowShareModal(true)}
                                    >
                                        {t('Share')}
                                    </Button>
                                    : null
                            }
                            {
                                currentAssignee ?
                                    <Button className="default-btn" size="sm"
                                        icon={<PlusOutlined />}
                                        onClick={() => {
                                            if (remainingCollaboratorSlots === 0) {
                                                message.warning(`Maximum ${MAX_COLLABORATORS} collaborators allowed for this step`)
                                                return
                                            }
                                            setShowCollaboratorModal(true)
                                        }}
                                    >
                                        {t('Add Collaborator')}
                                    </Button>
                                    : null
                            }

                        </Space>
                    </div>

                    <Form form={form} layout="vertical" onValuesChange={handleFormChange} scrollToFirstError={{ behavior: 'smooth', block: 'center' }}>
                        {
                            currentActiveForm?.items && currentActiveForm.items.map(data => {
                                return <Wrapper defaultFormValues={currentActiveForm?.defaultValues || {}} key={data.id} form={form} data={data} formElements={currentActiveForm?.items} allFormElements={allFormElements} formType={data.formType} conditions={currentFormConditions} previewMode={true} formValues={{ ...currentActiveForm?.defaultValues, ...currentValues, ...formValues }} spaceData={spaceData} requestDetail={requestDetail} defaultRequestDetail={defaultRequestDetail} currentStatus={currentStatus} passFormValues={rest.passFormValues} disableAllFields={isNonCurrentCycleView || hasSubmittedCurrentStep} freezeFields={freezeFields} lng={i18n.language} />
                            })
                        }
                    </Form>
                </div>
                : null
            }
            {
                currentAssignee ?
                    <Flex gap={16} className="approval-actions">
                        {
                            currentStep?.approvalStep ?
                                <>
                                    <Button className="approve-button" icon={<CheckOutlined />} onClick={submitForm}>{localizedStep?.approveLabel || "Approve"}</Button>
                                    <Button className="reject-button" icon={<CloseOutlined />} onClick={openRefusalModal}>{localizedStep?.rejectLabel || "Reject"}</Button> </>
                                : <Button className="approve-button" icon={<CheckOutlined />} onClick={submitForm}>{t('Submit')}</Button>
                        }
                    </Flex>
                    : null
            }
            <Modal title={isSubmitOnlyStep ? "Submission Details" : isApproving ? "Approval Details" : "Refusal Details"} open={showRefusalModal} onOk={cancelRefusal} onCancel={cancelRefusal} footer={false}>
                <div className="refusal-modal">
                    <Flex gap={8} align="center" style={{ marginBottom: "10px" }}>
                        <div className="card-label">
                            {
                                currentStep?.approvalStep ? <CheckOutlined style={{ color: "#0A99FE" }} /> : <EditOutlined style={{ color: "#0A99FE" }} />
                            }
                        </div>
                        <span>{localizedStep?.name}</span>
                    </Flex>
                    <Form form={refusalForm} layout="vertical" onFinish={refuseStep} >
                        <Form.Item label={isSubmitOnlyStep ? "Comment" : isApproving ? "Approval Comment" : "Refusal comment"} name="comment" rules={[{ required: isApproving ? false : true }]}>
                            <TextArea placeholder={t('Enter comment')} />
                        </Form.Item>
                        {
                            !isApproving ?
                                <>
                                    <Form.Item label="" name="refusalType">
                                        <Radio.Group>
                                            <Radio value="returnToStep">{t('Return to step')}</Radio>
                                            <Radio value="definitiveRefusal">{t('Definitive Refusal')}</Radio>
                                        </Radio.Group>
                                    </Form.Item>
                                    {
                                        refusalFormValues && refusalFormValues.refusalType === "returnToStep" ?
                                            <Form.Item label={t('Select step')} name="returnStep" placeholder={t('Select step')} rules={[{ required: true }]}>
                                                <Select options={returnSteps} />
                                            </Form.Item>
                                            : null
                                    }
                                </>
                                : null
                        }
                        <Flex justify="flex-end" gap={8}>
                            <Button onClick={cancelRefusal}>{t('Cancel')}</Button>
                            <Button className={isSubmitOnlyStep ? "submit-button" : isApproving ? "approve-button" : "reject-button"} htmlType="submit">
                                {isSubmitOnlyStep ? "Submit" : isApproving ? "Approve" : "Reject"}
                            </Button>
                        </Flex>
                    </Form>
                </div>
            </Modal>
            <Modal title={
                <Space>
                    <ShareAltOutlined />
                    <span>{t('Share Form:')} {currentActiveForm?.formHeader?.label}</span>
                </Space>
            } open={showShareModal} onOk={() => setShowShareModal(false)} onCancel={handleCloseRecipientModal} footer={false}>
                <div className=''>
                    <div className='mb-2'>{t('Recipients')}</div>
                    <Input placeholder={t('eg: test1@gmail.com, test2@gmail.com')} value={recipients} onChange={(e) => setRecipients(e.target.value)} />
                    {errorMessage ? <div className='text-red-500'>{errorMessage}</div> : null}
                    <div className='mb-2 mt-4'>{t('Form Language')}</div>
                    <Select
                        style={{ width: "100%" }}
                        value={shareLanguage}
                        onChange={setShareLanguage}
                        options={FORM_LANGUAGE_OPTIONS}
                    />
                    <div className="flex gap-2 items-center mt-4">
                        <Switch size="sm" checked={allowMultipleSubmissions} onChange={(checked) => setAllowMultipleSubmissions(checked)} />
                        <div className="text-[#2f2f2f]">{t('Allow multiple submissions')}</div>
                    </div>
                    <div className="flex gap-2 items-center mt-2">
                        <Switch size="sm" checked={allowPreviousForms} onChange={(checked) => setAllowPreviousForms(checked)} />
                        <div className="text-[#2f2f2f]">{t('Allow visibility of previous forms')}</div>
                    </div>

                    <Flex className='mt-4' justify="flex-end" gap={16}>
                        <Button className='submit-button' onClick={handleShareLink}>{t('Share')}</Button>
                        <Button onClick={handleCloseRecipientModal}>{t('Cancel')}</Button>
                    </Flex>
                </div>
            </Modal>
            <Modal
                title={`Add Collaborator (${remainingCollaboratorSlots} slot${remainingCollaboratorSlots !== 1 ? "s" : ""} remaining)`}
                open={showCollaboratorModal}
                onOk={() => setShowCollaboratorModal(false)}
                onCancel={() => {
                    setShowCollaboratorModal(false)
                    setCollaborators([{ name: "", email: "" }])
                    setCollaboratorErrors([])
                }}
                footer={false}
            >
                <div className="flex flex-col gap-3">
                    {collaborators.map((collab, index) => (
                        <Flex key={index} gap={8} align="center">
                            <Input
                                placeholder={t('Name')}
                                value={collab.name}
                                status={collaboratorErrors[index]?.name ? "error" : ""}
                                onChange={e => updateCollaboratorField(index, "name", e.target.value)}
                            />
                            <Input
                                placeholder={t('Email')}
                                value={collab.email}
                                status={collaboratorErrors[index]?.email ? "error" : ""}
                                onChange={e => updateCollaboratorField(index, "email", e.target.value)}
                            />
                            {collaborators.length > 1 && (
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeCollaboratorField(index)}
                                />
                            )}
                        </Flex>
                    ))}
                    <Button type="dashed" icon={<PlusOutlined />} onClick={addCollaboratorField} disabled={collaborators.length >= remainingCollaboratorSlots}>{t('Add Row')}</Button>
                    <Flex justify="flex-end" gap={8}>
                        <Button onClick={() => { setShowCollaboratorModal(false); setCollaborators([{ name: "", email: "" }]); setCollaboratorErrors([]) }}>{t('Cancel')}</Button>
                        <Button className="submit-button" onClick={handleAddCollaborator}>{t('Submit')}</Button>
                    </Flex>
                </div>
            </Modal>
            <SubmitMessage state={messageState} message={submitMessage} spaceId={requestDetail.spaceId} /> </Flex>
        </div>
    )
}

export default ApprovalDetails
